import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CalendarDays, MapPin, Clock, ArrowLeft, Minus, Plus } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { Eyebrow, Button } from "../components/Bits";
import { getEvent, buyTickets, apiError } from "../lib/api";

function fmtDate(iso) {
  try { return new Date(iso + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" }); }
  catch { return iso; }
}

export default function EventDetail() {
  const { slug } = useParams();
  const [e, setE] = useState(null);
  const [qty, setQty] = useState(1);
  const [buyer, setBuyer] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => { getEvent(slug).then(setE).catch(() => setNotFound(true)); }, [slug]);

  const buy = async (ev) => {
    ev.preventDefault();
    setBusy(true);
    try {
      const { checkout_url } = await buyTickets({ event_id: e.id, quantity: qty, buyer_name: buyer.name, buyer_email: buyer.email, origin_url: window.location.origin });
      window.location.href = checkout_url;
    } catch (err) { toast.error(apiError(err)); setBusy(false); }
  };

  if (notFound) return (
    <div className="pt-[74px] container-x py-32 text-center">
      <h1 className="font-display font-bold text-3xl text-nead-navy">Event not found</h1>
      <div className="mt-6"><Button to="/events" variant="pink">Back to events</Button></div>
    </div>
  );
  if (!e) return <div className="pt-[74px] container-x py-32 text-center text-nead-navy/40">Loading…</div>;

  const remaining = e.ticket_type === "self" && e.ticket_capacity ? e.ticket_capacity - (e.tickets_sold || 0) : null;
  const soldOut = remaining !== null && remaining <= 0;

  return (
    <div>
      <section className="relative overflow-hidden bg-nead-ink text-white pt-[74px]">
        <div className="absolute inset-0">
          {e.image && <img src={e.image} alt="" className="h-full w-full object-cover opacity-25 grayscale" />}
          <div className="absolute inset-0 bg-gradient-to-t from-nead-ink via-nead-ink/85 to-nead-ink/60" />
        </div>
        <div className="relative container-x pt-16 pb-16 md:pt-20 md:pb-20">
          <Link to="/events" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white mb-6"><ArrowLeft className="h-4 w-4" /> All events</Link>
          <Eyebrow className="text-nead-cyan">Event</Eyebrow>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mt-5 font-display font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.03] max-w-4xl">{e.title}</motion.h1>
          <div className="mt-7 flex flex-wrap gap-5 text-white/75">
            <span className="flex items-center gap-2"><CalendarDays className="h-5 w-5 text-nead-pink" />{fmtDate(e.date)}</span>
            {e.time && <span className="flex items-center gap-2"><Clock className="h-5 w-5 text-nead-pink" />{e.time}</span>}
            {e.venue && <span className="flex items-center gap-2"><MapPin className="h-5 w-5 text-nead-pink" />{e.venue}</span>}
          </div>
        </div>
      </section>

      <section className="container-x py-20 md:py-28">
        <div className="grid lg:grid-cols-[1.5fr_1fr] gap-14">
          <Reveal>
            <div className="prose-nead">
              {(e.description || "").split("\n").filter(Boolean).map((p, i) => (
                <p key={i} className="text-nead-navy/70 text-lg leading-relaxed mb-5">{p}</p>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="rounded-[1.5rem] border border-black/[0.07] bg-nead-mist p-7 lg:sticky lg:top-24">
              <h3 className="font-display font-bold text-xl text-nead-navy">Tickets</h3>
              {e.ticket_type === "none" && <p className="mt-3 text-nead-navy/60">This is a free event, no ticket required. Just turn up and enjoy.</p>}

              {e.ticket_type === "external" && (
                <div className="mt-4">
                  <p className="text-nead-navy/60">Tickets are available via our ticketing partner.</p>
                  <a href={e.ticket_url} target="_blank" rel="noreferrer" data-testid="event-external-tickets" className="mt-5 inline-flex items-center gap-2 rounded-full bg-nead-pink px-6 py-3 text-sm font-semibold text-white hover:bg-nead-navy transition-colors">
                    Buy tickets
                  </a>
                </div>
              )}

              {e.ticket_type === "self" && (
                soldOut ? (
                  <p className="mt-4 font-semibold text-nead-pink">Sold out.</p>
                ) : (
                  <form onSubmit={buy} className="mt-4 space-y-4" data-testid="event-ticket-form">
                    <div className="flex items-baseline justify-between">
                      <span className="font-display font-bold text-3xl text-nead-navy">£{Number(e.ticket_price).toFixed(2)}</span>
                      {remaining !== null && <span className="text-sm text-nead-navy/50">{remaining} left</span>}
                    </div>
                    <div className="flex items-center gap-3">
                      <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} className="h-10 w-10 rounded-full border border-black/15 flex items-center justify-center hover:border-nead-pink" data-testid="ticket-qty-minus"><Minus className="h-4 w-4" /></button>
                      <span className="w-8 text-center font-semibold text-nead-navy" data-testid="ticket-qty">{qty}</span>
                      <button type="button" onClick={() => setQty((q) => Math.min(remaining || 20, q + 1))} className="h-10 w-10 rounded-full border border-black/15 flex items-center justify-center hover:border-nead-pink" data-testid="ticket-qty-plus"><Plus className="h-4 w-4" /></button>
                    </div>
                    <input required placeholder="Your name" value={buyer.name} onChange={(ev) => setBuyer((b) => ({ ...b, name: ev.target.value }))} className="w-full rounded-lg border border-black/10 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-nead-pink" data-testid="ticket-name" />
                    <input required type="email" placeholder="Email address" value={buyer.email} onChange={(ev) => setBuyer((b) => ({ ...b, email: ev.target.value }))} className="w-full rounded-lg border border-black/10 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-nead-pink" data-testid="ticket-email" />
                    <button type="submit" disabled={busy} className="w-full inline-flex items-center justify-center rounded-full bg-nead-pink px-6 py-3 text-sm font-semibold text-white hover:bg-nead-navy transition-colors disabled:opacity-60" data-testid="ticket-checkout">
                      {busy ? "Redirecting…" : `Pay £${(Number(e.ticket_price) * qty).toFixed(2)}`}
                    </button>
                    <p className="text-xs text-nead-navy/45 text-center">Secure checkout powered by Stripe.</p>
                  </form>
                )
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
